import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useStagger } from '../hooks/useInView';
import Footer from '../components/sections/Footer';
import ProyectosDestacados from '../components/sections/ProyectosDestacados';
import WhatsAppLink from '../components/global/WhatsAppLink';
import CalendlyLink from '../components/global/CalendlyLink';
import { CTA_IDS, CTA_LOCATIONS } from '../lib/analytics.constants';

const modelos = [
  {
    id: 'proyecto',
    title: 'Proyecto',
    subtitle: 'Un desafío definido, resuelto de punta a punta',
    structure: 'Alcance cerrado · pago por etapas',
    nota: 'Empieza y termina: entregamos, garantizamos y queda tuyo.',
    ideal: 'Necesidades concretas con principio y fin claros: un sistema, una app, una integración.',
    includes: [
      'Discovery y alcance definido',
      'Roadmap y priorización',
      'Desarrollo por sprints con entregas continuas',
      'QA y testing incluido',
      'Deploy, garantía y documentación',
    ],
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
  },
  {
    id: 'partnership',
    title: 'Partnership',
    subtitle: 'Tu equipo de tecnología, todos los meses',
    structure: 'Fee mensual fijo · sin % de ingresos',
    nota: 'El paso siguiente al proyecto: la tecnología no se entrega y se abandona — evoluciona con vos, mes a mes.',
    ideal: 'Empresas que ya operan y necesitan evolución continua: mejoras, módulos nuevos, soporte y estrategia tecnológica.',
    includes: [
      'Equipo asignado y roadmap vivo',
      'Evolución continua del producto',
      'Soporte y mantenimiento prioritario',
      'Métricas y mejoras basadas en datos',
      'CTO-as-a-Service',
    ],
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
  },
  {
    id: 'revenue-share',
    title: 'Revenue Share',
    subtitle: 'Crecemos cuando vos crecés',
    structure: 'Fee base reducido + 1% a 18% de ingresos',
    nota: 'El paso siguiente al partnership: fee mínimo y participación — solo ganamos si tu producto genera.',
    ideal: 'Plataformas y productos digitales con proyección comercial clara y plan de crecimiento.',
    includes: [
      'Optimización continua basada en datos',
      'Reporte transparente',
      'Métricas de negocio integradas',
      'Arquitectura escalable desde el día uno',
      'Revisiones trimestrales de términos',
    ],
    highlight: true,
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    ),
  },
];

const Modelos = ({ footerRef }) => {
  const location = useLocation();
  const { ref: headRef, itemStyle: headStyle } = useStagger({ count: 3, stagger: 70 });
  const { ref: gridRef, itemStyle } = useStagger({ count: 3, stagger: 80 });

  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      const el = document.getElementById(hash.replace('#', ''));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  return (
    <>
      <Helmet>
        <title>Modelos de Desarrollo de Software en Paraguay · Proyecto, Partnership y Revenue Share | eien</title>
        <meta name="description" content="Tres modelos de colaboración para desarrollo de software a medida en Paraguay: Proyecto (alcance cerrado), Partnership (equipo tecnológico continuo) y Revenue Share (1–18% de ingresos)." />
        <link rel="canonical" href="https://eientech.io/modelos" />
      </Helmet>

      <section className="pt-28 pb-16 px-4 bg-gray-50 dark:bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto">

          {/* Header */}
          <div ref={headRef} className="text-center mb-12">
            <div className="flex justify-center">
              <p style={headStyle(0)} className="w-fit text-[10px] font-medium uppercase tracking-[0.2em] text-[#11b36d] bg-[#11b36d]/10 border border-[#11b36d]/20 rounded-full px-3 py-1 mb-5">Modelos de trabajo</p>
            </div>
            <h1 style={headStyle(1)} className="text-3xl sm:text-4xl md:text-5xl font-thin tracking-tight mb-4 text-[#0b2b40] dark:text-white">
              Cómo <span className="text-gradient text-short">trabajamos</span>
            </h1>
            <p style={headStyle(2)} className="text-slate-500 dark:text-slate-400 font-light max-w-xl mx-auto">
              Elegí el modelo que mejor acompañe tu visión. En todos los casos, trabajamos como tu aliado tecnológico.
            </p>
          </div>

          {/* 3-column model grid */}
          <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {modelos.map((modelo, i) => (
              <div
                key={modelo.id}
                id={modelo.id}
                style={itemStyle(i)}
                className={`flex flex-col rounded-2xl border p-7 scroll-mt-28 transition-colors duration-300 ${
                  modelo.highlight
                    ? 'bg-[#f4fdf8] dark:bg-[#0b1a10] border-[#11b36d]/45 hover:border-[#11b36d]/70 shadow-[0_0_28px_rgba(17,179,109,0.07)] dark:shadow-[0_0_32px_rgba(17,179,109,0.12)]'
                    : 'bg-white dark:bg-[#111111] border-gray-100 dark:border-white/[0.07] hover:border-[#11b36d]/40'
                }`}
              >
                {/* Top */}
                <div className="mb-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`inline-flex items-center justify-center p-2.5 rounded-lg ${
                      modelo.highlight
                        ? 'bg-[#11b36d]/15 border border-[#11b36d]/30 text-[#11b36d]'
                        : 'bg-[#11b36d]/10 border border-[#11b36d]/20 text-[#11b36d]'
                    }`}>
                      {modelo.icon}
                    </div>
                    {modelo.highlight && (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#11b36d]/10 border border-[#11b36d]/25 text-[#11b36d]">
                        Popular
                      </span>
                    )}
                  </div>
                  <h2 className="text-xl font-medium text-[#0b2b40] dark:text-white mb-1">{modelo.title}</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">{modelo.subtitle}</p>
                  <span className="inline-block px-3 py-1 rounded-full bg-[#11b36d]/10 border border-[#11b36d]/20 text-xs font-medium text-[#11b36d]">
                    {modelo.structure}
                  </span>
                  {modelo.nota && (
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-3 leading-relaxed">{modelo.nota}</p>
                  )}
                </div>

                {/* Divider */}
                <div className="border-t border-gray-100 dark:border-white/[0.06] mb-5" />

                {/* Ideal para */}
                <div className="mb-5">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-2">Ideal para</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{modelo.ideal}</p>
                </div>

                {/* Incluye */}
                <div className="flex-grow mb-6">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-3">Incluye</p>
                  <ul className="space-y-2">
                    {modelo.includes.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-[#11b36d] shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA */}
                <WhatsAppLink
                  ctaId={CTA_IDS.MODEL_CARD_WHATSAPP}
                  ctaLocation={CTA_LOCATIONS.MODEL_CARD}
                  modelName={modelo.id === 'revenue-share' ? 'revenue_share' : modelo.id}
                  message={`Hola, me interesa el modelo ${modelo.title}`}
                  className="inline-flex items-center justify-center gap-2 bg-[#11b36d] text-white px-5 py-3 rounded-full font-semibold text-sm hover:bg-[#0ea060] shadow-[0_2px_8px_rgba(17,179,109,0.22),inset_0_1px_0_rgba(255,255,255,0.12)] hover:shadow-[0_4px_14px_rgba(17,179,109,0.32),inset_0_1px_0_rgba(255,255,255,0.12)] active:scale-[0.97] transition-all duration-200 w-full"
                >
                  Quiero este modelo →
                </WhatsAppLink>
              </div>
            ))}
          </div>

          {/* ¿No sabés cuál elegir? — inline compact */}
          <div className="bg-white dark:bg-[#111111] border border-gray-100 dark:border-white/[0.07] rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-5 mb-12">
            <div>
              <p className="font-medium text-[#0b2b40] dark:text-white mb-1">¿No sabés cuál elegir?</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">Contanos tu idea y te ayudamos a identificar el modelo ideal para tu proyecto.</p>
            </div>
            <CalendlyLink
              ctaId={CTA_IDS.MODELOS_CALENDLY}
              ctaLocation={CTA_LOCATIONS.PAGE_CTA}
              className="shrink-0 inline-flex items-center gap-2 bg-[#11b36d] text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-[#0ea060] shadow-[0_2px_8px_rgba(17,179,109,0.22),inset_0_1px_0_rgba(255,255,255,0.12)] hover:shadow-[0_4px_14px_rgba(17,179,109,0.32),inset_0_1px_0_rgba(255,255,255,0.12)] active:scale-[0.97] transition-all duration-200"
            >
              Agendar sesión →
            </CalendlyLink>
          </div>

          <ProyectosDestacados />
        </div>
      </section>

      <section id="footer" ref={footerRef}>
        <Footer />
      </section>
    </>
  );
};

export default Modelos;
